import React, { useState, useEffect, useRef } from 'react';
import { Save, ArrowLeft, Palette, Type, Link, Image as ImageIcon, RotateCcw, Plus, Trash2, Settings, Upload, ShieldCheck, Edit3, Eye, Lock } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { GlassButton } from '../components/ui/GlassButton';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { templates, applyTemplateConfig, Template, CustomConfig } from '../lib/checkoutUtils';

const TemplateCard = ({ template, onClick }: { template: Template, onClick: () => void }) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [config] = useState<CustomConfig | null>(() => {
        try {
            const saved = localStorage.getItem(`custom_checkout_v2_${template.id}`);
            return saved ? JSON.parse(saved) : null;
        } catch (e) {
            console.error("Erro ao carregar config salva", e);
            return null;
        }
    });
    const [opacity, setOpacity] = useState(0);

    const handleLoad = () => {
        if (iframeRef.current && config) {
            const doc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
            if (doc) {
                applyTemplateConfig(doc, template, config);
            }
        }
        // Small delay to ensure styles are applied before showing
        setTimeout(() => setOpacity(1), 100);
    };

    return (
        <div className="t-card" onClick={onClick}>
            <div className="t-preview">
                <iframe
                    ref={iframeRef}
                    src={`${template.basePath}${template.files.html}`}
                    className="t-iframe"
                    scrolling="no"
                    onLoad={handleLoad}
                    style={{ opacity: opacity, transition: 'opacity 0.5s ease' }}
                />
                <span className="t-tag">{template.tag}</span>
            </div>
            <div className="t-details">
                <h3>{template.name}</h3>
                <p>{template.description}</p>
                <button className="t-btn-edit"><Edit3 size={16} /> Editar Template</button>
            </div>
        </div>
    );
};

export const Checkout: React.FC = () => {
    const { hasActiveSubscription } = useAuth();
    const [view, setView] = useState<'showcase' | 'editor' | 'templates'>('showcase');
    // Mobile Tab State
    const [mobileTab, setMobileTab] = useState<'editor' | 'preview'>('editor');

    const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
    const [checkoutId, setCheckoutId] = useState<string | null>(null);
    const [config, setConfig] = useState<CustomConfig>({
        texts: {
            title: '', subtitle: '', price: '', oldPrice: '', handle: '', ctaButton: 'PAGAR AGORA', footerNote: '',
            pixTitle: 'Pagamento via Pix', pixDesc: 'Receberá um QR Code para pagamento instantâneo.',
            cardBadgeText: 'CONTEÚDO EXCLUSIVO', namePlaceholder: 'Nome Completo', cpfPlaceholder: 'CPF', notifTitle: 'Nova venda realizada!'
        },
        styling: { primaryColor: '#ff0000', secondaryColor: '#000000', accentColor: '#00c6ff', ctaColor: '#00ff41', borderRadius: '12px' },
        media: { logo: '', cover: '', avatar: '', carousel: [] },
        links: {},

        features: {
            showTimer: true, showNotifications: true, showSocial: true, showBadges: true,
            notificationMode: 'random', manualNames: [], notificationCount: 5, timerMinutes: 15,
            notificationStyling: { bgColor: '#140000', textColor: '#eeeeee', iconColor: '#ff0000', msgTemplate: 'Acesso Liberado para <strong>{name}</strong>' }
        },
        plans: {
            plan1: { name: '1 mês', price: 'R$ 19,99', link: '#' },
            plan2: { name: '3 meses', price: 'R$ 39,99', link: '#' },
            plan3: { name: '6 meses', price: 'R$ 79,99', link: '#' }
        },
        socialVisibility: { instagram: true, tiktok: true },
        slug: ''
    });
    const [activeTab, setActiveTab] = useState<'geral' | 'estilo' | 'conteudo' | 'midia' | 'extras'>('geral');
    const [savedCheckouts, setSavedCheckouts] = useState<any[]>([]);
    const [slugAvailable, setSlugAvailable] = useState<boolean | 'checking' | null>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [iframeOpacity, setIframeOpacity] = useState(0);

    const forceUpdateNotification = (currentNames: string[], forcedName?: string) => {
        setTimeout(() => {
            const doc = iframeRef.current?.contentDocument || iframeRef.current?.contentWindow?.document;
            if (doc && (doc.defaultView as any).customNotificationNames) {
                (doc.defaultView as any).customNotificationNames = currentNames;
                if ((doc.defaultView as any).restartNotifications) (doc.defaultView as any).restartNotifications(forcedName);
            }
        }, 100);
    };

    const handleEdit = (template: Template) => {
        setIframeOpacity(0);
        setSelectedTemplate(template);
        setSlugAvailable(null);
        const saved = localStorage.getItem(`custom_checkout_v2_${template.id}`);
        if (saved) {
            const parsed = JSON.parse(saved);
            setConfig({
                ...config,
                ...parsed,
                texts: { ...config.texts, ...parsed.texts }
            });
        } else {
            // Configurações específicas por template
            if (template.id === '01') {
                // Template Larissa - Checkout Clássico
                setConfig({
                    texts: {
                        title: 'OFERTA RELÂMPAGO EXPIRA EM:', subtitle: 'ACESSO VITALÍCIO', price: 'POR APENAS R$ 29,90', oldPrice: 'DE R$ 99,90', handle: '', ctaButton: 'PAGAR AGORA', footerNote: 'Grupo VIP no Telegram',
                        pixTitle: 'Pagamento via Pix', pixDesc: 'Ao clicar em "Pagar Agora", você receberá um QR Code para pagamento instantâneo.',
                        cardBadgeText: 'CONTEÚDOS EXCLUSIVOS', namePlaceholder: 'Nome Completo', cpfPlaceholder: 'CPF', notifTitle: 'Nova venda realizada!'
                    },
                    styling: { primaryColor: '#ff0000', secondaryColor: '#050505', accentColor: '#00ff41', ctaColor: '#00ff41', borderRadius: '10px' },
                    media: { logo: '', cover: '', avatar: '', carousel: [] },
                    links: { social: 'https://t.me/panta_tips' },
                    features: {
                        showTimer: true, showNotifications: true, showSocial: true, showBadges: true,
                        notificationMode: 'random', manualNames: [], notificationCount: 5, timerMinutes: 15,
                        notificationStyling: { bgColor: '#140000', textColor: '#eeeeee', iconColor: '#ff0000', msgTemplate: 'Acesso Liberado para <strong>{name}</strong>' }
                    },
                    plans: {
                        plan1: { name: '', price: '', link: '' },
                        plan2: { name: '', price: '', link: '' },
                        plan3: { name: '', price: '', link: '' }
                    },
                    socialVisibility: { instagram: true, tiktok: true }
                });
            } else if (template.id === '02') {
                // Template VIIH - Perfil Premium
                setConfig({
                    texts: {
                        title: 'Ah vhiivii', subtitle: 'Novinha, rabuda 😏 e cheia de vontade de te deixar maluco...', price: 'R$ 19,99', oldPrice: '', handle: '@Ahvhiivii', ctaButton: '1 mês', footerNote: '',
                        pixTitle: '', pixDesc: '',
                        cardBadgeText: '', namePlaceholder: '', cpfPlaceholder: '', notifTitle: ''
                    },
                    styling: { primaryColor: '#FF8C5A', secondaryColor: '#F5F5F5', accentColor: '#FF9B6A', ctaColor: '#FF8C5A', borderRadius: '50px' },
                    media: { logo: '', cover: '', avatar: '', carousel: [] },
                    links: { instagram: 'https://www.instagram.com/vhiiviireserva', tiktok: 'https://www.tiktok.com/@vhiiviibr' },
                    features: {
                        showTimer: false, showNotifications: false, showSocial: true, showBadges: false,
                        notificationMode: 'random', manualNames: [], notificationCount: 0, timerMinutes: 0,
                        notificationStyling: { bgColor: '', textColor: '', iconColor: '', msgTemplate: '' }
                    },
                    plans: {
                        plan1: { name: '1 mês', price: 'R$ 19,99', link: '#' },
                        plan2: { name: '3 meses', price: 'R$ 39,99', link: '#' },
                        plan3: { name: '6 meses (20% off)', price: 'R$ 79,99', link: '#' }
                    },
                    socialVisibility: { instagram: true, tiktok: true }
                });
            }
        }

        // Definir aba inicial correta
        if (template.id === '02') {
            setActiveTab('conteudo');
        } else {
            setActiveTab('geral');
        }
        setView('editor');
    };

    const handleCarouselUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length > 0) {
            const newImages: string[] = [];
            let loadedCount = 0;
            files.forEach(file => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    if (typeof reader.result === 'string') {
                        newImages.push(reader.result);
                    }
                    loadedCount++;
                    if (loadedCount === files.length) {
                        setConfig(prev => ({
                            ...prev,
                            media: { ...prev.media, carousel: [...prev.media.carousel, ...newImages] }
                        }));
                    }
                };
                reader.readAsDataURL(file);
            });
        }
    };

    const removeCarouselImage = (index: number) => {
        setConfig(prev => ({
            ...prev,
            media: { ...prev.media, carousel: prev.media.carousel.filter((_, i) => i !== index) }
        }));
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, field: keyof CustomConfig['media']) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setConfig(prev => ({
                    ...prev,
                    media: { ...prev.media, [field]: reader.result as string }
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    useEffect(() => {
        if (!config.slug || view !== 'editor') {
            setSlugAvailable(null);
            return;
        }

        const timer = setTimeout(async () => {
            setSlugAvailable('checking');
            try {
                const res = await api.get(`/checkouts/check-slug/${config.slug}`, {
                    params: { excludeId: checkoutId }
                });
                setSlugAvailable(res.data.available);
            } catch (error) {
                console.error("Erro ao validar slug", error);
                setSlugAvailable(null);
            }
        }, 600);

        return () => clearTimeout(timer);
    }, [config.slug, view, checkoutId]);

    const handleSave = async () => {
        if (!selectedTemplate) return;

        if (!config.slug) {
            window.alert('Por favor, preencha o slug da oferta.');
            return;
        }

        if (slugAvailable === false) {
            window.alert('Este slug já está em uso. Por favor, escolha outro.');
            return;
        }

        try {
            const payload = {
                slug: config.slug,
                subdomain: 'checkout', // Padronizado como checkout
                template_id: selectedTemplate.id,
                config: config
            };

            let res;
            if (checkoutId) {
                res = await api.put(`/checkouts/${checkoutId}`, payload);
            } else {
                res = await api.post('/checkouts', payload);
                setCheckoutId(res.data.id);
            }

            window.alert('Checkout salvo com sucesso no banco de dados!');
        } catch (error: any) {
            console.error("Erro ao salvar checkout", error);
            window.alert('Erro ao salvar: ' + (error.response?.data?.error || error.message));
        }
    };

    useEffect(() => {
        if (view === 'showcase' && hasActiveSubscription) {
            fetchCheckouts();
        }
    }, [view, hasActiveSubscription]);

    const fetchCheckouts = async () => {
        try {
            const res = await api.get('/checkouts');
            setSavedCheckouts(res.data);
        } catch (error) {
            console.error("Erro ao buscar checkouts", error);
        }
    };

    const handleEditSaved = (checkout: any) => {
        setCheckoutId(checkout.id);
        const template = templates.find(t => t.id === checkout.template_id);
        if (template) {
            setSelectedTemplate(template);
            setConfig({
                ...checkout.config,
                slug: checkout.slug
            });
            setView('editor');
            setActiveTab('geral');
            setSlugAvailable(true); // Se já está salvo, o slug atual é válido
        }
    };

    useEffect(() => {
        if (view !== 'editor' || !iframeRef.current) return;
        const updateIframe = () => {
            const doc = iframeRef.current?.contentDocument || iframeRef.current?.contentWindow?.document;
            if (!doc || !selectedTemplate) return;

            applyTemplateConfig(doc, selectedTemplate, config);

            // 9. Timer Dinâmico (Mantido aqui pois depende de doc.body.dataset que não foi abstraído ou pode ser específico de runtime)
            // Na verdade, applyTemplateConfig não tem o Timer logic completo se ele depender de estado anterior do DOM não persistido?
            // Verificando applyTemplateConfig... ele não tem o bloco do Timer Dinâmico completo (linhas 627-633 do original)
            // Vou reinserir o bloco do timer aqui ou mover para applyTemplateConfig se fizer sentido.
            // O bloco original do timer usava doc.body.dataset.lastTimer. 
            // Vou manter o Timer aqui para segurança maxima de não quebrar funcionalidades complexas.

            const currentTimer = config.features.timerMinutes?.toString() || '15';
            if (doc.body.dataset.lastTimer !== currentTimer) {
                if ((doc.defaultView as any).restartMainTimer) {
                    (doc.defaultView as any).restartMainTimer(config.features.timerMinutes || 15);
                    doc.body.dataset.lastTimer = currentTimer;
                }
            }

            // Reveal iframe gently
            setIframeOpacity(1);
        };

        const interval = setInterval(updateIframe, 800);
        return () => clearInterval(interval);
    }, [config, view, selectedTemplate]);

    return (
        <div className="ultimate-checkout animate-fade">
            {view === 'showcase' ? (
                <div className="showcase-view">
                    {!hasActiveSubscription ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
                            <div className="glass-card" style={{
                                textAlign: 'center',
                                padding: '50px 40px',
                                maxWidth: '440px',
                                width: '100%',
                                margin: '0 auto',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '24px'
                            }}>
                                <div style={{
                                    width: '80px',
                                    height: '80px',
                                    borderRadius: '50%',
                                    background: 'rgba(255, 106, 0, 0.1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '1px solid rgba(255, 106, 0, 0.2)',
                                    boxShadow: '0 0 30px rgba(255, 106, 0, 0.15)'
                                }}>
                                    <Lock size={40} style={{ color: '#ff6a00' }} />
                                </div>

                                <div>
                                    <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#fff', marginBottom: '12px' }}>
                                        Assinatura necessária
                                    </h2>
                                    <p style={{ color: '#a1a1aa', fontSize: '15px', lineHeight: 1.6, margin: 0 }}>
                                        Assine um plano da plataforma para acessar o Checkout e os demais recursos.
                                    </p>
                                </div>

                                <RouterLink to="/plans" style={{ width: '100%', textDecoration: 'none' }}>
                                    <div className="glass-button-wrap" style={{ width: '100%' }}>
                                        <button className="glass-button" style={{ width: '100%', padding: '14px' }}>
                                            <span className="glass-button-text">Ver planos</span>
                                        </button>
                                        <div className="glass-button-shadow" style={{ width: '100%' }}></div>
                                    </div>
                                </RouterLink>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="top-bar">
                                <div>
                                    <h1 className="page-title">Checkout Customizado</h1>
                                    <p style={{ color: '#666', fontSize: '14px' }}>Gerencie seus links de checkout e templates ativos.</p>
                                </div>

                                <div className="top-actions">
                                    <GlassButton onClick={() => setView('templates')} style={{ width: 'auto' }}>
                                        <Plus size={16} /> Novo Link
                                    </GlassButton>
                                </div>
                            </div>

                            <div style={{ marginTop: '40px' }}>
                                <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '20px' }}>Meus Checkouts</h2>
                                <div className="template-grid-m" style={{ marginBottom: '40px' }}>
                                    {savedCheckouts.length > 0 ? (
                                        savedCheckouts.map(c => (
                                            <div key={c.id} className="t-card" onClick={() => handleEditSaved(c)}>
                                                <div className="t-preview" style={{ height: '150px' }}>
                                                    <div style={{ padding: '20px', background: '#111', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                                        <div style={{ color: '#ff6a00', fontWeight: 900, fontSize: '1.2rem' }}>checkout.hotlize.com</div>
                                                        <div style={{ color: '#666', fontSize: '0.9rem' }}>/{c.slug}</div>
                                                    </div>
                                                    <span className="t-tag" style={{ background: '#333' }}>Template {c.template_id}</span>
                                                </div>
                                                <div className="t-details">
                                                    <h3>{c.config.texts.title || 'Checkout sem título'}</h3>
                                                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                                        <button className="t-btn-edit" style={{ flex: 1 }}><Edit3 size={14} /> Editar</button>
                                                        <a
                                                            href={typeof window !== 'undefined' && window.location.hostname === 'localhost'
                                                                ? `${window.location.origin}/checkout/${c.slug}`
                                                                : `https://checkout.hotlize.com/${c.slug}`}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="t-btn-edit"
                                                            style={{ flex: 1, background: '#ff6a00', borderColor: '#ff6a00' }}
                                                            onClick={e => e.stopPropagation()}
                                                        >
                                                            <Link size={14} /> Abrir
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '40px', background: 'rgba(255,255,255,0.02)', borderRadius: '24px', border: '1px dashed #222' }}>
                                            <p style={{ color: '#666' }}>Você ainda não criou nenhum checkout customizado.</p>
                                        </div>
                                    )}
                                </div>

                                <div style={{ marginTop: '60px' }}>
                                    <h2 style={{ fontSize: '1.2rem', fontWeight: 600, color: '#444', marginBottom: '20px' }}>Templates Disponíveis</h2>
                                    <div className="template-grid-m" style={{ marginBottom: '80px', opacity: 0.8 }}>
                                        {templates.map(t => (
                                            <TemplateCard key={t.id} template={t} onClick={() => {
                                                setCheckoutId(null);
                                                handleEdit(t);
                                            }} />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            ) : view === 'templates' ? (
                <div className="showcase-view animate-fade">
                    <div className="top-bar">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <button className="btn-icon" onClick={() => setView('showcase')}><ArrowLeft size={18} /></button>
                            <div>
                                <h1 className="page-title">Escolher Template</h1>
                                <p style={{ color: '#666', fontSize: '14px' }}>Selecione o design base para sua nova oferta.</p>
                            </div>
                        </div>
                    </div>

                    <div className="template-grid-m" style={{ marginTop: '40px' }}>
                        {templates.map(t => (
                            <TemplateCard key={t.id} template={t} onClick={() => {
                                setCheckoutId(null);
                                handleEdit(t);
                            }} />
                        ))}
                    </div>
                </div>
            ) : (
                <div className="builder-view">
                    <header className="builder-header">
                        <div className="builder-left">
                            <button className="btn-icon" onClick={() => setView(checkoutId ? 'showcase' : 'templates')}><ArrowLeft size={18} /></button>
                            <span>Editor: <strong>{selectedTemplate?.name}</strong></span>
                        </div>

                        {/* Mobile Tabs Switcher */}
                        <div className="mobile-tabs-switcher">
                            <button
                                className={`mt-tab ${mobileTab === 'editor' ? 'active' : ''}`}
                                onClick={() => setMobileTab('editor')}
                            >
                                <Settings size={14} /> Editar
                            </button>
                            <button
                                className={`mt-tab ${mobileTab === 'preview' ? 'active' : ''}`}
                                onClick={() => setMobileTab('preview')}
                            >
                                <Eye size={14} /> Preview
                            </button>
                        </div>

                        <div className="builder-right">
                            <button className="btn-secondary" onClick={() => {
                                if (window.confirm('Deseja resetar todas as configurações para o padrão?')) {
                                    localStorage.removeItem(`custom_checkout_v2_${selectedTemplate?.id}`);
                                    // Buscar versão fresca do template para garantir novos seletores
                                    const freshTemplate = templates.find(t => t.id === selectedTemplate?.id) || selectedTemplate!;
                                    handleEdit(freshTemplate);
                                }
                            }}><RotateCcw size={14} /> Resetar</button>
                            <button className="btn-primary-v" onClick={handleSave}><Save size={16} /> Salvar Alterações</button>
                        </div>
                    </header>

                    <main className="builder-main">
                        {/* Editor Panel - Visible if Desktop OR (Mobile AND mobileTab=editor) */}
                        <div className={`builder-container ${mobileTab === 'editor' ? 'active' : ''}`}>
                            <nav className="builder-tabs">
                                <button className={activeTab === 'geral' ? 'active' : ''} onClick={() => setActiveTab('geral')} title="Configurações de URL"><Settings size={20} /></button>
                                <button className={activeTab === 'conteudo' ? 'active' : ''} onClick={() => setActiveTab('conteudo')} title="Conteúdo"><Type size={20} /></button>
                                <button className={activeTab === 'midia' ? 'active' : ''} onClick={() => setActiveTab('midia')} title="Mídia"><ImageIcon size={20} /></button>
                                {selectedTemplate?.id === '01' && (
                                    <>
                                        <button className={activeTab === 'estilo' ? 'active' : ''} onClick={() => setActiveTab('estilo')} title="Estilo"><Palette size={20} /></button>
                                        <button className={activeTab === 'extras' ? 'active' : ''} onClick={() => setActiveTab('extras')} title="Segurança"><ShieldCheck size={20} /></button>
                                    </>
                                )}
                            </nav>

                            <aside className="builder-options scroll-beauty">
                                {activeTab === 'geral' && (
                                    <div className="option-group">
                                        <h3>Configurações de URL</h3>
                                        <div className="input-item">
                                            <label>Domínio de Checkout</label>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                <input
                                                    type="text"
                                                    value="checkout.hotlize.com"
                                                    disabled
                                                    style={{ opacity: 0.5, cursor: 'not-allowed' }}
                                                />
                                            </div>
                                        </div>
                                        <div className="input-item">
                                            <label>Slug da Oferta (ex: pack-premium)</label>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', position: 'relative' }}>
                                                <span style={{ fontSize: '12px', color: '#666' }}>/</span>
                                                <input
                                                    type="text"
                                                    placeholder="slug-da-oferta"
                                                    value={config.slug || ''}
                                                    onChange={e => setConfig({ ...config, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                                                    style={{
                                                        borderColor: slugAvailable === false ? '#ff0000' :
                                                            slugAvailable === true ? '#00ff41' : '#1a1a1a'
                                                    }}
                                                />
                                                {slugAvailable === 'checking' && (
                                                    <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                                                        <div className="mini-spinner"></div>
                                                    </div>
                                                )}
                                            </div>
                                            {slugAvailable === false && (
                                                <p style={{ color: '#ff0000', fontSize: '10px', marginTop: '5px' }}>Este slug já está em uso.</p>
                                            )}
                                            {slugAvailable === true && (
                                                <p style={{ color: '#00ff41', fontSize: '10px', marginTop: '5px' }}>Slug disponível!</p>
                                            )}
                                        </div>

                                        {selectedTemplate?.id === '01' && (
                                            <>
                                                <h3 style={{ marginTop: '30px' }}>Gatilhos e Exibição</h3>
                                                <div className="toggle-item">
                                                    <span>Exibir Cronômetro (Header)</span>
                                                    <input type="checkbox" checked={config.features.showTimer} onChange={e => setConfig({ ...config, features: { ...config.features, showTimer: e.target.checked } })} />
                                                </div>
                                                <div className="input-item">
                                                    <label>Tempo (Minutos)</label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        max="60"
                                                        value={config.features.timerMinutes || 15}
                                                        onChange={e => setConfig({ ...config, features: { ...config.features, timerMinutes: parseInt(e.target.value) || 1 } })}
                                                    />
                                                </div>
                                                <div className="toggle-item">
                                                    <span>Redes Sociais</span>
                                                    <input type="checkbox" checked={config.features.showSocial} onChange={e => setConfig({ ...config, features: { ...config.features, showSocial: e.target.checked } })} />
                                                </div>
                                                <div className="toggle-item">
                                                    <span>Badge de Destaque</span>
                                                    <input type="checkbox" checked={config.features.showBadges} onChange={e => setConfig({ ...config, features: { ...config.features, showBadges: e.target.checked } })} />
                                                </div>
                                                <div className="input-item" style={{ marginTop: '20px' }}>
                                                    <label>Título da Notificação de Venda</label>
                                                    <input type="text" value={config.texts.notifTitle} onChange={e => setConfig({ ...config, texts: { ...config.texts, notifTitle: e.target.value } })} />
                                                </div>

                                                <div className="input-group-field">
                                                    <h4>Configuração de Notificações</h4>
                                                    <div className="input-item">
                                                        <label>Lista de Nomes</label>
                                                        <div className="notification-toggle-wrapper">
                                                            <div className="nt-header">
                                                                <span className="nt-label">Modo de Geração</span>
                                                                <div className="nt-status">{config.features.notificationMode === 'random' ? 'Aleatório (Auto)' : 'Manual'}</div>
                                                            </div>
                                                            <label className="switch-container">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={config.features.notificationMode === 'manual'}
                                                                    onChange={e => setConfig({ ...config, features: { ...config.features, notificationMode: e.target.checked ? 'manual' : 'random' } })}
                                                                />
                                                                <span className="slider"></span>
                                                            </label>
                                                        </div>

                                                        {config.features.notificationMode === 'manual' && (
                                                            <div className="manual-names-list">
                                                                {config.features.manualNames.map((name, index) => (
                                                                    <div key={index} className="name-input-row" style={{ display: 'flex', gap: '5px', marginBottom: '5px' }}>
                                                                        <input
                                                                            type="text"
                                                                            value={name}
                                                                            onChange={e => {
                                                                                const newNames = [...config.features.manualNames];
                                                                                newNames[index] = e.target.value;
                                                                                setConfig({ ...config, features: { ...config.features, manualNames: newNames } });
                                                                            }}
                                                                            onBlur={e => forceUpdateNotification(config.features.manualNames, e.target.value)}
                                                                            placeholder="Nome do Comprador"
                                                                        />
                                                                        <button
                                                                            className="btn-secondary"
                                                                            style={{ padding: '5px 10px' }}
                                                                            onClick={() => {
                                                                                const newNames = config.features.manualNames.filter((_, i) => i !== index);
                                                                                setConfig({ ...config, features: { ...config.features, manualNames: newNames } });
                                                                                forceUpdateNotification(newNames); // No specific name forced on remove, just refresh
                                                                            }}
                                                                        >
                                                                            <Trash2 size={14} />
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                                <button
                                                                    className="btn-primary-v"
                                                                    style={{ width: '100%', marginTop: '5px' }}
                                                                    onClick={() => {
                                                                        const newNames = [...config.features.manualNames, 'Novo Comprador'];
                                                                        setConfig({ ...config, features: { ...config.features, manualNames: newNames } });
                                                                        forceUpdateNotification(newNames, 'Novo Comprador');
                                                                    }}
                                                                >
                                                                    + Adicionar Nome
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="input-item">
                                                        <label>Template da Mensagem (Use {"{name}"} para o nome)</label>
                                                        <input
                                                            type="text"
                                                            value={config.features.notificationStyling.msgTemplate}
                                                            onChange={e => setConfig({ ...config, features: { ...config.features, notificationStyling: { ...config.features.notificationStyling, msgTemplate: e.target.value } } })}
                                                        />
                                                    </div>
                                                    <div className="color-row" style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                                        <div className="color-control">
                                                            <label>Fundo</label>
                                                            <div className="color-box">
                                                                <input type="color" value={config.features.notificationStyling.bgColor} onChange={e => setConfig({ ...config, features: { ...config.features, notificationStyling: { ...config.features.notificationStyling, bgColor: e.target.value } } })} />
                                                                <span>Cor</span>
                                                            </div>
                                                        </div>
                                                        <div className="color-control">
                                                            <label>Texto</label>
                                                            <div className="color-box">
                                                                <input type="color" value={config.features.notificationStyling.textColor} onChange={e => setConfig({ ...config, features: { ...config.features, notificationStyling: { ...config.features.notificationStyling, textColor: e.target.value } } })} />
                                                                <span>Cor</span>
                                                            </div>
                                                        </div>
                                                        <div className="color-control">
                                                            <label>Ícone</label>
                                                            <div className="color-box">
                                                                <input type="color" value={config.features.notificationStyling.iconColor} onChange={e => setConfig({ ...config, features: { ...config.features, notificationStyling: { ...config.features.notificationStyling, iconColor: e.target.value } } })} />
                                                                <span>Cor</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                {activeTab === 'conteudo' && (
                                    <div className="option-group">
                                        <h3>Textos e Copy</h3>

                                        {selectedTemplate?.id === '01' ? (
                                            // Campos para Template Larissa (Checkout)
                                            <>
                                                <div className="input-item">
                                                    <label>Título Principal / Timer Label</label>
                                                    <input type="text" value={config.texts.title} onChange={e => setConfig({ ...config, texts: { ...config.texts, title: e.target.value } })} />
                                                </div>
                                                <div className="input-item">
                                                    <label>Subtítulo / Badge</label>
                                                    <input type="text" value={config.texts.subtitle} onChange={e => setConfig({ ...config, texts: { ...config.texts, subtitle: e.target.value } })} />
                                                </div>
                                                <div className="input-item">
                                                    <label>Banner Promocional (Rodapé do Preço)</label>
                                                    <input type="text" value={config.texts.footerNote} onChange={e => setConfig({ ...config, texts: { ...config.texts, footerNote: e.target.value } })} />
                                                </div>
                                                <div className="price-row">
                                                    <div className="input-item">
                                                        <label>Preço "De"</label>
                                                        <input type="text" value={config.texts.oldPrice} onChange={e => setConfig({ ...config, texts: { ...config.texts, oldPrice: e.target.value } })} />
                                                    </div>
                                                    <div className="input-item">
                                                        <label>Preço "Por"</label>
                                                        <input type="text" value={config.texts.price} onChange={e => setConfig({ ...config, texts: { ...config.texts, price: e.target.value } })} />
                                                    </div>
                                                </div>
                                                <div className="input-item">
                                                    <label>Texto do Botão (CTA)</label>
                                                    <input type="text" value={config.texts.ctaButton} onChange={e => setConfig({ ...config, texts: { ...config.texts, ctaButton: e.target.value } })} />
                                                </div>
                                                <div className="input-group-field">
                                                    <h4>Placeholder do Formulário</h4>
                                                    <div className="input-item">
                                                        <label>Campo Nome</label>
                                                        <input type="text" value={config.texts.namePlaceholder} onChange={e => setConfig({ ...config, texts: { ...config.texts, namePlaceholder: e.target.value } })} />
                                                    </div>
                                                    <div className="input-item">
                                                        <label>Campo CPF</label>
                                                        <input type="text" value={config.texts.cpfPlaceholder} onChange={e => setConfig({ ...config, texts: { ...config.texts, cpfPlaceholder: e.target.value } })} />
                                                    </div>
                                                </div>
                                                <div className="input-group-field">
                                                    <h4>Preço único (valor cobrado no PIX)</h4>
                                                    <p className="builder-field-desc">O Classic Pro exibe um único plano. Este é o valor cobrado quando o cliente gera o PIX.</p>
                                                    <div className="input-item">
                                                        <label>Nome do plano (opcional)</label>
                                                        <input type="text" value={config.plans.plan1.name} onChange={e => setConfig({ ...config, plans: { ...config.plans, plan1: { ...config.plans.plan1, name: e.target.value } } })} placeholder="Ex: Acesso vitalício" />
                                                    </div>
                                                    <div className="input-item">
                                                        <label>Preço (ex: R$ 29,90)</label>
                                                        <input type="text" value={config.plans.plan1.price} onChange={e => setConfig({ ...config, plans: { ...config.plans, plan1: { ...config.plans.plan1, price: e.target.value } } })} placeholder="R$ 0,00" />
                                                    </div>
                                                    <div className="input-item">
                                                        <label>Link após pagamento (opcional)</label>
                                                        <input type="text" value={config.plans.plan1.link} onChange={e => setConfig({ ...config, plans: { ...config.plans, plan1: { ...config.plans.plan1, link: e.target.value } } })} placeholder="https://t.me/seugrupo" />
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            // Campos para Template VIIH (Perfil de Criador)
                                            <>
                                                <div className="input-item">
                                                    <label>Nome do Criador</label>
                                                    <input type="text" value={config.texts.title} onChange={e => setConfig({ ...config, texts: { ...config.texts, title: e.target.value } })} placeholder="Ex: Ah vhiivii" />
                                                </div>
                                                <div className="input-item">
                                                    <label>Handle (@usuario)</label>
                                                    <input type="text" value={config.texts.handle} onChange={e => setConfig({ ...config, texts: { ...config.texts, handle: e.target.value } })} placeholder="Ex: @Ahvhiivii" />
                                                </div>
                                                <div className="input-item">
                                                    <label>Bio / Descrição</label>
                                                    <textarea
                                                        rows={5}
                                                        value={config.texts.subtitle}
                                                        onChange={e => setConfig({ ...config, texts: { ...config.texts, subtitle: e.target.value } })}
                                                        placeholder="Descreva o perfil..."
                                                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', resize: 'vertical' }}
                                                    />
                                                </div>

                                                <div className="input-group-field">
                                                    <h4>Redes Sociais</h4>
                                                    <div className="toggle-item">
                                                        <span>Mostrar Instagram</span>
                                                        <input type="checkbox" checked={config.socialVisibility.instagram} onChange={e => setConfig({ ...config, socialVisibility: { ...config.socialVisibility, instagram: e.target.checked } })} />
                                                    </div>
                                                    {config.socialVisibility.instagram && (
                                                        <div className="input-item">
                                                            <label>Link Instagram</label>
                                                            <input type="text" value={config.links.instagram || ''} onChange={e => setConfig({ ...config, links: { ...config.links, instagram: e.target.value } })} />
                                                        </div>
                                                    )}

                                                    <div className="toggle-item">
                                                        <span>Mostrar TikTok</span>
                                                        <input type="checkbox" checked={config.socialVisibility.tiktok} onChange={e => setConfig({ ...config, socialVisibility: { ...config.socialVisibility, tiktok: e.target.checked } })} />
                                                    </div>
                                                    {config.socialVisibility.tiktok && (
                                                        <div className="input-item">
                                                            <label>Link TikTok</label>
                                                            <input type="text" value={config.links.tiktok || ''} onChange={e => setConfig({ ...config, links: { ...config.links, tiktok: e.target.value } })} />
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="input-group-field">
                                                    <h4>Planos de Assinatura</h4>

                                                    {/* Plano 1 */}
                                                    <div style={{ background: '#f9f9f9', padding: '10px', borderRadius: '8px', marginBottom: '10px' }}>
                                                        <strong>Plano 1 (Destaque)</strong>
                                                        <div className="input-item" style={{ marginTop: '5px' }}>
                                                            <label>Nome</label>
                                                            <input type="text" value={config.plans.plan1.name} onChange={e => setConfig({ ...config, plans: { ...config.plans, plan1: { ...config.plans.plan1, name: e.target.value } } })} />
                                                        </div>
                                                        <div className="input-item">
                                                            <label>Preço (ex: R$ 19,99)</label>
                                                            <input type="text" value={config.plans.plan1.price} onChange={e => setConfig({ ...config, plans: { ...config.plans, plan1: { ...config.plans.plan1, price: e.target.value } } })} />
                                                        </div>
                                                        <div className="input-item">
                                                            <label>Link após pagamento (ex: t.me/grupo)</label>
                                                            <input type="text" value={config.plans.plan1.link} onChange={e => setConfig({ ...config, plans: { ...config.plans, plan1: { ...config.plans.plan1, link: e.target.value } } })} placeholder="https://t.me/seugrupo" />
                                                        </div>
                                                    </div>

                                                    {/* Plano 2 */}
                                                    <div style={{ background: '#f9f9f9', padding: '10px', borderRadius: '8px', marginBottom: '10px' }}>
                                                        <strong>Plano 2</strong>
                                                        <div className="input-item" style={{ marginTop: '5px' }}>
                                                            <label>Nome</label>
                                                            <input type="text" value={config.plans.plan2.name} onChange={e => setConfig({ ...config, plans: { ...config.plans, plan2: { ...config.plans.plan2, name: e.target.value } } })} />
                                                        </div>
                                                        <div className="input-item">
                                                            <label>Preço (ex: R$ 39,99)</label>
                                                            <input type="text" value={config.plans.plan2.price} onChange={e => setConfig({ ...config, plans: { ...config.plans, plan2: { ...config.plans.plan2, price: e.target.value } } })} />
                                                        </div>
                                                        <div className="input-item">
                                                            <label>Link após pagamento</label>
                                                            <input type="text" value={config.plans.plan2.link} onChange={e => setConfig({ ...config, plans: { ...config.plans, plan2: { ...config.plans.plan2, link: e.target.value } } })} placeholder="https://t.me/seugrupo" />
                                                        </div>
                                                    </div>

                                                    {/* Plano 3 */}
                                                    <div style={{ background: '#f9f9f9', padding: '10px', borderRadius: '8px', marginBottom: '10px' }}>
                                                        <strong>Plano 3</strong>
                                                        <div className="input-item" style={{ marginTop: '5px' }}>
                                                            <label>Nome</label>
                                                            <input type="text" value={config.plans.plan3.name} onChange={e => setConfig({ ...config, plans: { ...config.plans, plan3: { ...config.plans.plan3, name: e.target.value } } })} />
                                                        </div>
                                                        <div className="input-item">
                                                            <label>Preço (ex: R$ 79,99)</label>
                                                            <input type="text" value={config.plans.plan3.price} onChange={e => setConfig({ ...config, plans: { ...config.plans, plan3: { ...config.plans.plan3, price: e.target.value } } })} />
                                                        </div>
                                                        <div className="input-item">
                                                            <label>Link após pagamento</label>
                                                            <input type="text" value={config.plans.plan3.link} onChange={e => setConfig({ ...config, plans: { ...config.plans, plan3: { ...config.plans.plan3, link: e.target.value } } })} placeholder="https://t.me/seugrupo" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                {activeTab === 'midia' && (
                                    <div className="option-group">
                                        <h3>Upload de Mídia</h3>

                                        {selectedTemplate?.id === '01' ? (
                                            // Template Larissa - Carrossel + Logo
                                            <>
                                                <div className="upload-section">
                                                    <label>Carrossel de Imagens (Add)</label>
                                                    <div className="carousel-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '10px' }}>
                                                        {config.media.carousel.map((img, idx) => (
                                                            <div key={idx} className="c-thumb" style={{ position: 'relative', height: '60px', borderRadius: '8px', overflow: 'hidden' }}>
                                                                <img src={img} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                                <button onClick={() => removeCarouselImage(idx)} style={{ position: 'absolute', top: 0, right: 0, background: 'rgba(0,0,0,0.7)', color: '#fff', border: 'none', cursor: 'pointer', padding: '2px' }}><Trash2 size={12} /></button>
                                                            </div>
                                                        ))}
                                                        <div className="upload-card" onClick={() => document.getElementById('u-carousel')?.click()} style={{ height: '60px' }}>
                                                            <Plus size={20} color="#444" />
                                                        </div>
                                                    </div>
                                                    <input type="file" id="u-carousel" hidden multiple onChange={handleCarouselUpload} />
                                                </div>

                                                <div className="upload-section">
                                                    <label>Logotipo do Checkout</label>
                                                    <div className="upload-card" onClick={() => document.getElementById('u-logo')?.click()}>
                                                        {config.media.logo ? <img src={config.media.logo} alt="Logo" /> : <div className="u-pl"><Upload size={20} /><span>Subir Logo</span></div>}
                                                    </div>
                                                    <input type="file" id="u-logo" hidden onChange={e => handleFileUpload(e, 'logo')} />
                                                    <p style={{ fontSize: '10px', color: '#666', marginTop: '5px' }}>Logo exibido no header do checkout</p>
                                                </div>
                                            </>
                                        ) : (
                                            // Template VIIH - Apenas Cover e Avatar
                                            <>
                                                <div className="upload-section">
                                                    <label>Imagem de Capa (Cover)</label>
                                                    <div className="upload-card" onClick={() => !config.media.cover && document.getElementById('u-cover')?.click()} style={{ position: 'relative' }}>
                                                        {config.media.cover ? (
                                                            <>
                                                                <img src={config.media.cover} alt="Cover" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); setConfig({ ...config, media: { ...config.media, cover: '' } }); }}
                                                                    style={{ position: 'absolute', top: 5, right: 5, background: 'rgba(0,0,0,0.7)', color: '#fff', border: 'none', borderRadius: '50%', padding: '5px', cursor: 'pointer', zIndex: 10 }}
                                                                >
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <div className="u-pl"><Upload size={20} /><span>Subir Capa</span></div>
                                                        )}
                                                    </div>
                                                    <input type="file" id="u-cover" hidden onChange={e => handleFileUpload(e, 'cover')} />
                                                    <p style={{ fontSize: '10px', color: '#666', marginTop: '5px' }}>
                                                        <strong>Mobile:</strong> 375x288px (Preview). <br />
                                                        <strong>Desktop:</strong> 1024x288px. <br />
                                                        <span style={{ color: '#ff0000' }}>⚠ Mantenha o conteúdo no centro para não cortar no celular.</span>
                                                    </p>
                                                </div>

                                                <div className="upload-section">
                                                    <label>Foto de Perfil (Avatar)</label>
                                                    <div className="upload-card" onClick={() => !config.media.avatar && document.getElementById('u-avatar')?.click()} style={{ position: 'relative' }}>
                                                        {config.media.avatar ? (
                                                            <>
                                                                <img src={config.media.avatar} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); setConfig({ ...config, media: { ...config.media, avatar: '' } }); }}
                                                                    style={{ position: 'absolute', top: 5, right: 5, background: 'rgba(0,0,0,0.7)', color: '#fff', border: 'none', borderRadius: '50%', padding: '5px', cursor: 'pointer', zIndex: 10 }}
                                                                >
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <div className="u-pl"><Upload size={20} /><span>Subir Avatar</span></div>
                                                        )}
                                                    </div>
                                                    <input type="file" id="u-avatar" hidden onChange={e => handleFileUpload(e, 'avatar')} />
                                                    <p style={{ fontSize: '10px', color: '#666', marginTop: '5px' }}>
                                                        <strong>Tamanho:</strong> 500x500px (Quadrado). <br />
                                                        <span style={{ color: '#ff0000' }}>⚠ O corte é circular: centralize o rosto/logo.</span>
                                                    </p>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                {activeTab === 'estilo' && (
                                    <div className="option-group">
                                        <h3>Cores e Bordas</h3>
                                        <div className="color-control">
                                            <label>Cor de Destaque (Principal)</label>
                                            <div className="color-box">
                                                <input type="color" value={config.styling.primaryColor} onChange={e => setConfig({ ...config, styling: { ...config.styling, primaryColor: e.target.value } })} />
                                                <span>{config.styling.primaryColor}</span>
                                            </div>
                                        </div>
                                        <div className="color-control">
                                            <label>Cor do Botão (CTA)</label>
                                            <div className="color-box">
                                                <input type="color" value={config.styling.ctaColor} onChange={e => setConfig({ ...config, styling: { ...config.styling, ctaColor: e.target.value } })} />
                                                <span>{config.styling.ctaColor}</span>
                                            </div>
                                        </div>
                                        <div className="color-control">
                                            <label>Cor de Fundo da Página</label>
                                            <div className="color-box">
                                                <input type="color" value={config.styling.secondaryColor} onChange={e => setConfig({ ...config, styling: { ...config.styling, secondaryColor: e.target.value } })} />
                                                <span>{config.styling.secondaryColor}</span>
                                            </div>
                                        </div>
                                        <div className="input-item">
                                            <label>Arredondamento das Bordas</label>
                                            <select value={config.styling.borderRadius} onChange={e => setConfig({ ...config, styling: { ...config.styling, borderRadius: e.target.value } })}>
                                                <option value="0px">Retangular</option>
                                                <option value="8px">Leve</option>
                                                <option value="16px">Moderno</option>
                                                <option value="30px">Muito Arredondado</option>
                                                <option value="50px">Pílula</option>
                                            </select>
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'extras' && (
                                    <div className="option-group">
                                        <h3>Card de Pagamento Pix</h3>
                                        <div className="input-item">
                                            <label>Título do Card (Pix)</label>
                                            <input type="text" value={config.texts.pixTitle} onChange={e => setConfig({ ...config, texts: { ...config.texts, pixTitle: e.target.value } })} />
                                        </div>
                                        <div className="input-item">
                                            <label>Descrição do Pagamento</label>
                                            <textarea rows={3} value={config.texts.pixDesc} onChange={e => setConfig({ ...config, texts: { ...config.texts, pixDesc: e.target.value } })} />
                                        </div>
                                    </div>
                                )}
                            </aside>
                        </div>

                        {/* Preview Panel - Visible if Desktop OR (Mobile AND mobileTab=preview) */}
                        <section className={`builder-preview ${mobileTab === 'preview' ? 'active' : ''}`}>
                            <div className="mobile-frame">
                                <div className="frame-header">
                                    <div className="frame-dot"></div>
                                </div>
                                <iframe
                                    ref={iframeRef}
                                    src={`${selectedTemplate?.basePath}${selectedTemplate?.files.html}`}
                                    className="frame-iframe"
                                    style={{ opacity: iframeOpacity, transition: 'opacity 0.4s ease-in-out' }}
                                />
                            </div>
                        </section>
                    </main>
                </div>
            )
            }

            <style>{`
                .ultimate-checkout { color: #fff; font-family: 'Inter', sans-serif; }
                .showcase-view { padding: 0; max-width: 1400px; margin: 0 auto; }

                .template-grid-m { display: grid; grid-template-columns: repeat(auto-fill, minmax(350px, 1fr)); gap: 30px; margin-top: 20px; }
                .t-card { background: #080808; border-radius: 24px; border: 1px solid #111; overflow: hidden; cursor: pointer; transition: 0.3s; }
                .t-card:hover { border-color: #ff0000; transform: translateY(-5px); }
                .t-preview { height: 260px; position: relative; background: #020202; overflow: hidden; }
                .t-iframe { width: 400%; height: 400%; transform: scale(0.25); transform-origin: 0 0; border: none; pointer-events: none; }
                .t-tag { position: absolute; top: 15px; right: 15px; background: #ff0000; color: #fff; padding: 4px 12px; border-radius: 50px; font-size: 10px; font-weight: 900; }
                .t-details { padding: 25px; }
                .t-details h3 { font-size: 1.2rem; margin-bottom: 5px; }
                .t-details p { color: #444; font-size: 0.85rem; margin-bottom: 20px; }
                .t-btn-edit { width: 100%; padding: 12px; background: #111; border: 1px solid #222; color: #fff; border-radius: 12px; font-weight: 700; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 10px; transition: 0.2s; }
                .t-btn-edit:hover { background: #ff0000; border-color: #ff0000; }

                /* BUILDER */
                .builder-view { min-height: 100vh; display: flex; flex-direction: column; background: #000; } /* Background black for builder mode only */
                .builder-header { height: 60px; background: #080808; border-bottom: 1px solid #111; display: flex; align-items: center; justify-content: space-between; padding: 0 20px; position: sticky; top: 0; z-index: 100; }
                .builder-left { display: flex; align-items: center; gap: 15px; color: #444; font-size: 13px; }
                .builder-right { display: flex; gap: 10px; }
                .btn-icon { background: none; border: none; color: #fff; cursor: pointer; padding: 5px; }
                .btn-primary-v { background: #ff0000; color: #fff; border: none; padding: 8px 16px; border-radius: 8px; font-weight: 800; font-size: 13px; display: flex; align-items: center; gap: 8px; cursor: pointer; }
                .btn-secondary { background: #111; border: 1px solid #222; color: #555; padding: 8px 16px; border-radius: 8px; font-size: 12px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 6px; }

                .builder-main { flex: 1; display: flex; position: relative; }
                .builder-container { display: flex; flex: 1; } /* Wrapper for tabs+options */
                
                /* Mobile Tabs Logic */
                .mobile-tabs-switcher { display: none; gap: 8px; margin: 0 16px; background: #111; padding: 4px; border-radius: 8px; }
                .mt-tab { background: none; border: none; color: #666; padding: 6px 12px; border-radius: 6px; font-size: 12px; font-weight: 600; display: flex; alignItems: center; gap: 6px; cursor: pointer; }
                .mt-tab.active { background: #222; color: #fff; }

                /* Desktop Default */
                .builder-tabs { width: 64px; background: #050505; border-right: 1px solid #111; display: flex; flex-direction: column; align-items: center; padding: 20px 0; gap: 20px; position: sticky; top: 60px; height: calc(100vh - 60px); }
                .builder-options { width: 380px; background: #080808; padding: 25px; border-right: 1px solid #111; }
                .builder-preview { flex: 1; background: #020202; display: flex; align-items: center; justify-content: center; padding: 40px; position: sticky; top: 60px; height: calc(100vh - 60px); }

                @media (max-width: 1024px) {
                    .builder-header { padding: 0 10px; }
                    .mobile-tabs-switcher { display: flex; }
                    .builder-left span { display: none; } /* Hide "Editor: Template Name" on mobile to save space */
                    
                    /* Stack Logic */
                    .builder-main { flex-direction: column; }
                    
                    /* Toggle Visibility based on state classes */
                    .builder-container { display: none; width: 100%; height: 100%; }
                    .builder-container.active { display: flex; }
                    
                    .builder-preview { display: none; width: 100%; height: 100%; padding: 20px; }
                    .builder-preview.active { display: flex; }

                    /* Adjust widths for mobile */
                    .builder-options { width: 100%; border-right: none; padding: 20px; }
                    .builder-tabs { display: none; } /* Hide side tabs, maybe move to top or bottom if needed, but for now we rely on activeTab state which is tricky without tabs visible. 
                       Wait, if tabs are hidden, user cant switch sections. 
                       Let's keep tabs visible but horizontal or smaller. */
                    
                    .builder-container { flex-direction: column; }
                    .builder-tabs { 
                        display: flex; 
                        width: 100%; 
                        height: auto; 
                        flex-direction: row; 
                        justify-content: space-around; 
                        border-right: none; 
                        border-bottom: 1px solid #111; 
                        padding: 10px 0;
                        order: -1; /* Tabs on top */
                    }
                    
                    .mobile-frame { width: 100%; height: 100%; border: none; border-radius: 0; box-shadow: none; }
                    .frame-header, .frame-dot { display: none; } /* Maximize screen for preview */
                    .frame-iframe { height: 100%; }
                }

                .builder-tabs button { background: none; border: none; color: #222; cursor: pointer; transition: 0.2s; }
                .builder-tabs button.active { color: #ff0000; }
                .builder-tabs button:hover { color: #fff; }

                .builder-options { width: 380px; background: #080808; padding: 25px; overflow-y: auto; border-right: 1px solid #111; }
                .option-group h3 { font-size: 10px; text-transform: uppercase; letter-spacing: 2px; color: #ff0000; margin-bottom: 25px; font-weight: 900; }
                .input-item { margin-bottom: 18px; }
                .input-item label { display: block; font-size: 10px; color: #444; margin-bottom: 8px; font-weight: 700; text-transform: uppercase; }
                .input-item input, .input-item textarea, .input-item select { width: 100%; background: #020202; border: 1px solid #1a1a1a; color: #fff; padding: 12px; border-radius: 10px; outline: none; font-size: 13px; }
                .input-item input:focus { border-color: #ff0000; }
                
                .toggle-item { display: flex; justify-content: space-between; align-items: center; background: #040404; padding: 12px; border-radius: 12px; margin-bottom: 10px; font-size: 13px; color: #666; }
                .price-row { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
                .input-group-field { margin-top: 30px; padding-top: 20px; border-top: 1px solid #111; }
                .input-group-field h4 { font-size: 11px; margin-bottom: 15px; color: #fff; }
                .builder-field-desc { font-size: 12px; color: #888; margin-bottom: 16px; line-height: 1.4; }

                .color-control { margin-bottom: 20px; }
                .color-box { display: flex; align-items: center; gap: 12px; background: #020202; padding: 8px; border-radius: 8px; }
                .color-box input { width: 34px; height: 34px; border: none; background: none; cursor: pointer; }
                .color-box span { font-family: monospace; font-size: 12px; color: #444; }

                .upload-section { margin-bottom: 20px; }
                .upload-card { width: 80px; height: 80px; border: 2px dashed #111; border-radius: 16px; display: flex; align-items: center; justify-content: center; overflow: hidden; cursor: pointer; }
                .upload-card img { width: 100%; height: 100%; object-fit: contain; }
                .u-pl { display: flex; flex-direction: column; align-items: center; gap: 8px; color: #222; font-size: 10px; font-weight: 800; }

                .builder-preview { flex: 1; background: #020202; display: flex; align-items: center; justify-content: center; padding: 20px; position: sticky; top: 60px; height: calc(100vh - 60px); }
                .mobile-frame { 
                    width: 340px; 
                    height: 680px; 
                    background: #000; 
                    border: 12px solid #121212; 
                    border-radius: 50px; 
                    position: relative; 
                    overflow: hidden; 
                    box-shadow: 0 50px 100px rgba(0,0,0,0.9), 0 0 20px rgba(255,255,255,0.02); 
                    transition: transform 0.3s ease;
                }
                .mobile-frame::after {
                    content: "";
                    position: absolute;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background: linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 50%);
                    pointer-events: none;
                    z-index: 5;
                }
                .frame-header { 
                    height: 32px; 
                    background: transparent; 
                    display: flex; 
                    align-items: center; 
                    justify-content: center; 
                    position: absolute; 
                    top: 0; 
                    left: 0; 
                    right: 0; 
                    z-index: 10;
                }
                .frame-dot { 
                    width: 80px; 
                    height: 22px; 
                    background: #000; 
                    border-radius: 20px; 
                    margin-top: 5px;
                    border: 1px solid #111;
                }
                .frame-iframe { width: 100%; height: 100%; border: none; background: #fff; }

                .scroll-beauty::-webkit-scrollbar { width: 4px; }
                .scroll-beauty::-webkit-scrollbar-thumb { background: #222; border-radius: 10px; }
                .animate-fade { animation: fadeIn 0.4s ease; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

                /* Fix: Robust Switch UI */
                .notification-toggle-wrapper { background: #111; padding: 15px; border-radius: 12px; border: 1px solid #222; margin-bottom: 20px; display: flex; align-items: center; justify-content: space-between; }
                .nt-header { display: flex; flex-direction: column; gap: 4px; }
                .nt-label { color: #fff; font-size: 0.9rem; font-weight: 600; }
                .nt-status { color: #888; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; }

                .switch-container { position: relative; display: inline-block; width: 50px; height: 26px; }
                .switch-container input { opacity: 0; width: 0; height: 0; }
                .slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #333; transition: .4s; border-radius: 34px; border: 1px solid #444; }
                .slider:before { position: absolute; content: ""; height: 18px; width: 18px; left: 3px; bottom: 3px; background-color: white; transition: .4s; border-radius: 50%; box-shadow: 0 2px 5px rgba(0,0,0,0.3); }
                input:checked + .slider { background-color: #ff0000; border-color: #ff0000; }
                input:checked + .slider:before { transform: translateX(24px); }

                /* List Styling */
                .manual-names-list { background: #111; padding: 10px; border-radius: 8px; border: 1px solid #222; max-height: 200px; overflow-y: auto; margin-bottom: 20px; margin-top: 10px; }
                .manual-names-list::-webkit-scrollbar { width: 4px; }
                .manual-names-list::-webkit-scrollbar-thumb { background: #333; border-radius: 10px; }
                .name-input-row input { background: #080808 !important; border: 1px solid #333 !important; font-size: 0.85rem !important; padding: 8px 12px !important; height: 36px; }
                .name-input-row input:focus { border-color: #ff0000 !important; }

                .mini-spinner {
                    width: 14px;
                    height: 14px;
                    border: 2px solid rgba(255,255,255,0.1);
                    border-top-color: #ff6a00;
                    border-radius: 50%;
                    animation: spin 0.8s linear infinite;
                }
                @keyframes spin { to { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
};
